import React, { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import DraftingPanel from "@/components/DraftingPanel";
import ResearchChat from "@/components/ResearchChat";
import PredicateSearch from "@/components/PredicateSearch";
import MaudePanel from "@/components/MaudePanel";
import DocumentLibrary from "@/components/DocumentLibrary";
import ProjectsPanel from "@/components/ProjectsPanel";
import PatentsPanel from "@/components/PatentsPanel";
import ChangeControlPanel from "@/components/ChangeControlPanel";
import ClinicalTrialsPanel from "@/components/ClinicalTrialsPanel";
import ChuckConsentPanel from "@/components/ChuckConsentPanel";
import DrugResearchPanel from "@/components/DrugResearchPanel";
import ClassificationWizard from "@/components/ClassificationWizard";
import OnboardingWizard from "@/components/OnboardingWizard";
import QmsPanel from "@/components/QmsPanel";
import RiskMatrixPanel from "@/components/RiskMatrixPanel";
import DesignControlsPanel from "@/components/DesignControlsPanel";
import CerPanel from "@/components/CerPanel";
import TherapeuticsPanel from "@/components/TherapeuticsPanel";
import TherapeuticsDraftingPanel from "@/components/TherapeuticsDraftingPanel";
import AuthGate from "@/components/AuthGate";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { WORKSPACE } from "@/constants/testIds";
import {
  FileSignature,
  MessagesSquare,
  Scale,
  ShieldAlert,
  Library,
  Folder,
  Fingerprint,
  GitBranchPlus,
  Microscope,
  Pill,
  Compass,
  FileCog,
  Layers,
  Beaker,
  Award,
  Pipette,
} from "lucide-react";

const TABS = [
  { key: "projects", label: "Projects", icon: Folder, testid: "workspace-tab-projects" },
  { key: "classify", label: "Classify", icon: Compass, testid: "workspace-tab-classify" },
  { key: "draft", label: "Drafting", icon: FileSignature, testid: WORKSPACE.tabDrafting },
  { key: "chat", label: "Research Chat", icon: MessagesSquare, testid: WORKSPACE.tabResearch },
  { key: "predicate", label: "Predicate Search", icon: Scale, testid: WORKSPACE.tabPredicate },
  { key: "drug", label: "Therapeutic Research", icon: Pill, testid: "workspace-tab-drug" },
  { key: "clinical", label: "Clinical Trials", icon: Microscope, testid: "workspace-tab-clinical" },
  { key: "patents", label: "Patents & IP", icon: Fingerprint, testid: "workspace-tab-patents" },
  { key: "changectrl", label: "Change Control", icon: GitBranchPlus, testid: "workspace-tab-changectrl" },
  { key: "risk", label: "Risk Matrix", icon: ShieldAlert, testid: "workspace-tab-risk" },
  { key: "design", label: "Design Controls", icon: Layers, testid: "workspace-tab-design" },
  { key: "cer", label: "CER Generator", icon: Beaker, testid: "workspace-tab-cer" },
  { key: "ther-draft", label: "Therapeutics Drafting", icon: Pipette, testid: "workspace-tab-ther-draft" },
  { key: "ther-intel", label: "Therapeutics Intel", icon: Award, testid: "workspace-tab-ther-intel" },
  { key: "qms", label: "QMS SOPs", icon: FileCog, testid: "workspace-tab-qms" },
  { key: "maude", label: "MAUDE Events", icon: ShieldAlert, testid: WORKSPACE.tabMaude },
  { key: "library", label: "Library", icon: Library, testid: WORKSPACE.tabLibrary },
];

const ADMIN_TABS = [];   // Admin console moved to a dedicated /admin route.

export default function Workspace() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("projects");
  const [preset, setPreset] = useState(null);   // { project_id, device_name }
  const [libraryRefresh, setLibraryRefresh] = useState(0);
  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState("");
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [labelAnchor, setLabelAnchor] = useState(null);   // {setid, brand, generic, marketer, title}

  const handleLabelAnchor = (anchor) => {
    setLabelAnchor(anchor);
    setTab("ther-draft");
  };

  useEffect(() => {
    if (user && !user.onboarding_completed) {
      setOnboardingOpen(true);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    api.get("/projects").then(({ data }) => {
      setProjects(data);
      if (!activeProjectId && data.length > 0) setActiveProjectId(data[0].id);
    }).catch(() => {});
    // eslint-disable-next-line
  }, [user]);

  const gotoDraftWithPreset = (p) => {
    setPreset(p);
    if (p?.project_id) setActiveProjectId(p.project_id);
    setTab("draft");
  };

  const activeProject = projects.find((p) => p.id === activeProjectId);
  const showGate = !loading && !user;
  const tabs = user?.is_admin ? [...TABS, ...ADMIN_TABS] : TABS;

  return (
    <div data-testid={WORKSPACE.root} className="min-h-screen bg-[#FAFAF7] text-slate-900">
      <Navbar />

      <div className={`mx-auto flex max-w-[1600px] flex-col gap-0 px-4 lg:flex-row lg:px-6 ${showGate ? "pointer-events-none blur-sm select-none" : ""}`}>
        <aside className="lg:sticky lg:top-[70px] lg:h-[calc(100vh-70px)] lg:w-64 lg:shrink-0 lg:border-r lg:border-slate-200 lg:pr-4 lg:pt-8">
          <div className="overline mb-4 hidden text-slate-400 lg:block">WORKSPACE</div>
          <nav className="flex flex-row gap-1 overflow-x-auto py-4 lg:flex-col lg:overflow-visible lg:py-0">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  data-testid={t.testid}
                  onClick={() => { setPreset(null); setTab(t.key); }}
                  className={`group flex shrink-0 items-center gap-3 border px-4 py-3 text-left text-sm font-medium tracking-wide transition-all ${
                    active
                      ? "border-[#2563EB] bg-[#2563EB]/10 text-white"
                      : "border-transparent text-slate-500 hover:border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <Icon
                    className={`h-4 w-4 ${active ? "text-[#2563EB]" : "text-slate-400 group-hover:text-slate-700"}`}
                    strokeWidth={2}
                  />
                  {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="min-h-[calc(100vh-70px)] flex-1 py-6 lg:py-10 lg:pl-8">
          {user && tab === "projects" && <ProjectsPanel onCreateDraft={gotoDraftWithPreset} />}
          {user && tab === "classify" && <ClassificationWizard />}
          {user && tab === "draft" && (
            <DraftingPanel
              preset={preset}
              onDraftCreated={() => setLibraryRefresh((v) => v + 1)}
            />
          )}
          {user && tab === "chat" && <ResearchChat />}
          {user && tab === "predicate" && <PredicateSearch />}
          {user && tab === "patents" && <PatentsPanel />}
          {user && tab === "changectrl" && <ChangeControlPanel />}
          {user && tab === "clinical" && <ClinicalTrialsPanel />}
          {user && tab === "maude" && <MaudePanel />}
          {user && tab === "library" && <DocumentLibrary key={libraryRefresh} />}
          {user && tab === "drug" && <DrugResearchPanel />}
          {user && tab === "qms" && <QmsPanel />}
          {user && tab === "risk" && (
            <div>
              {projects.length > 1 && (
                <div className="mb-4 flex items-center gap-2 text-xs">
                  <span className="overline text-slate-500">PROJECT</span>
                  <select
                    data-testid="risk-project-select"
                    value={activeProjectId}
                    onChange={(e) => setActiveProjectId(e.target.value)}
                    className="border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900"
                  >
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <RiskMatrixPanel projectId={activeProjectId} project={activeProject} />
            </div>
          )}
          {user && tab === "design" && (
            <div>
              {projects.length > 1 && (
                <div className="mb-4 flex items-center gap-2 text-xs">
                  <span className="overline text-slate-500">PROJECT</span>
                  <select
                    data-testid="dc-project-select"
                    value={activeProjectId}
                    onChange={(e) => setActiveProjectId(e.target.value)}
                    className="border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900"
                  >
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <DesignControlsPanel projectId={activeProjectId} project={activeProject} />
            </div>
          )}
          {user && tab === "cer" && (
            <div>
              {projects.length > 1 && (
                <div className="mb-4 flex items-center gap-2 text-xs">
                  <span className="overline text-slate-500">PROJECT</span>
                  <select
                    data-testid="cer-project-select"
                    value={activeProjectId}
                    onChange={(e) => setActiveProjectId(e.target.value)}
                    className="border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900"
                  >
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <CerPanel project={activeProject} />
            </div>
          )}
          {user && tab === "ther-draft" && <TherapeuticsDraftingPanel project={activeProject} labelAnchor={labelAnchor} onClearAnchor={() => setLabelAnchor(null)} />}
          {user && tab === "ther-intel" && <TherapeuticsPanel onAnchor={handleLabelAnchor} />}
          {!user && !loading && (
            <div className="flex h-96 items-center justify-center text-slate-300">
              <span className="overline">SIGN IN TO VIEW</span>
            </div>
          )}
        </main>
      </div>

      {showGate && <AuthGate />}
      <OnboardingWizard
        open={onboardingOpen}
        onDone={() => setOnboardingOpen(false)}
      />
    </div>
  );
}
